// src/contexts/SyncContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import SyncService from '../services/syncService';

const SyncContext = createContext();

export function useSync() {
  return useContext(SyncContext);
}

export function SyncProvider({ children }) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingOperations, setPendingOperations] = useState(0);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [syncError, setSyncError] = useState(null);

  // Update online status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setSyncError(null);
    };
    
    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Update sync status periodically
  useEffect(() => {
    const updateSyncStatus = () => {
      const status = SyncService.getSyncStatus();
      setPendingOperations(status.pendingOperations);
      setLastSyncTime(new Date());
    };

    const interval = setInterval(updateSyncStatus, 5000); // Update every 5 seconds
    updateSyncStatus(); // Initial update

    return () => clearInterval(interval);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      SyncService.cleanup();
    };
  }, []);

  const value = {
    isOnline,
    pendingOperations,
    lastSyncTime,
    syncError,
    setSyncError,
    syncService: SyncService
  };

  return (
    <SyncContext.Provider value={value}>
      {children}
    </SyncContext.Provider>
  );
}